import { useCallback, useContext, useMemo } from 'react'
import { debounce } from 'lodash'
import { QuestionnaireResponse } from '@medplum/fhirtypes'
import AssessmentLayoutContext, { AutosaveStatus } from 'contexts/AssessmentLayoutContext'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { UpdateAssessmentUncontrolledFunction } from 'components/Assessments/hooks/useUpdateAssessment'

interface UseAutosaveOnChangeParams {
  response: QuestionnaireResponse | null
}

type UseAutosaveOnChangeFunction = (
  _changedValues: Record<string, unknown>,
  allValues: Record<string, unknown>
) => void

/**
 * A custom hook that returns an autosave handler for use with Ant Design Form's onValuesChange.
 * This approach is more efficient than using Form.useWatch + useEffect as it doesn't cause re-renders.
 *
 * @param {UpdateAssessmentUncontrolledFunction} saveFunction - The function to be used for saving data
 * @param {UseAutosaveOnChangeParams} params - Additional parameters needed for the save function
 * @param {number} [delay=5000] - The debounce delay in milliseconds
 * @returns {Function} A handler function to be passed to Form's onValuesChange prop
 */
const useAutosaveOnChange = (
  saveFunction: UpdateAssessmentUncontrolledFunction,
  params: UseAutosaveOnChangeParams,
  delay = 5000
): UseAutosaveOnChangeFunction => {
  const { cantEditAssessmentFields } = useAssessmentContext()
  const { setAutosaveStatus } = useContext(AssessmentLayoutContext)

  const onSave = useCallback(
    async (allValues: Record<string, unknown>) => {
      try {
        await saveFunction({
          formValues: allValues,
          response: params.response
        })

        setAutosaveStatus(AutosaveStatus.SUCCESS)
      } catch (e) {
        setAutosaveStatus(AutosaveStatus.ERROR)
      }
    },
    [saveFunction, params.response, setAutosaveStatus]
  )

  // Memoize the debounced save function so it doesn't get recreated on every render
  const debouncedOnSave = useMemo(
    () => debounce((allValues: Record<string, unknown>) => onSave(allValues), delay),
    [delay, onSave]
  )

  // Return the handler that will be called by onValuesChange
  const handleValuesChange = useCallback(
    (_changedValues: Record<string, unknown>, allValues: Record<string, unknown>) => {
      if (cantEditAssessmentFields) return

      setAutosaveStatus(AutosaveStatus.SAVING)
      void debouncedOnSave(allValues)
    },
    [debouncedOnSave, cantEditAssessmentFields, setAutosaveStatus]
  )

  return handleValuesChange
}

export default useAutosaveOnChange
